import React from 'react';
import { BadgeCheckIcon, BriefcaseBusinessIcon, GraduationCapIcon, HeadsetIcon, HouseIcon, MegaphoneIcon, ShoppingBagIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { demoIdentity } from '../../data/landingVisual';

/**
 * One identity, connected outward.
 *
 * The credential on the left is demonstration data and says so. It shows the
 * distinction the product actually enforces: what a verified academic standing
 * asserts is printed on the card, and nothing more is claimed.
 */
const CONNECTED = [{
  icon: GraduationCapIcon,
  label: 'Academics',
  position: 'left-1/2 top-0 -translate-x-1/2'
}, {
  icon: HouseIcon,
  label: 'Housing',
  position: 'right-2 top-8'
}, {
  icon: MegaphoneIcon,
  label: 'Campus',
  position: 'right-0 top-1/2 -translate-y-1/2'
}, {
  icon: BriefcaseBusinessIcon,
  label: 'Opportunities',
  position: 'right-2 bottom-8'
}, {
  icon: ShoppingBagIcon,
  label: 'Marketplace',
  position: 'left-1/2 bottom-0 -translate-x-1/2'
}, {
  icon: HeadsetIcon,
  label: 'Services',
  position: 'left-2 bottom-8'
}, {
  icon: WalletIcon,
  label: 'Money',
  position: 'left-0 top-1/2 -translate-y-1/2'
}];
export function IdentityBanner() {
  return <section aria-labelledby="identity-heading" className="bg-canvas pb-16 lg:pb-20" data-id="element-2232" data-mp="14mlsosvzlqma">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-2233" data-mp="14kho08q9knky">
        <div className="relative overflow-hidden rounded-panel bg-brand-900 px-5 py-10 sm:px-8 lg:px-12 lg:py-12" data-id="element-2234" data-mp="19k42elhtw9vc">
          {/* Quiet field, so the credential and the ring both read cleanly. */}
          <div aria-hidden className="pointer-events-none absolute -left-16 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-white/10" data-id="element-2235" data-mp="zo0q4mt9iuuo" />

          <div className="relative grid gap-10 lg:grid-cols-[minmax(0,auto)_minmax(0,1fr)_minmax(0,auto)] lg:items-center" data-id="element-2236" data-mp="qnm4fmcdzdqn">
            {/* Digital credential */}
            <div className="mx-auto w-full max-w-[19rem]" data-id="element-2237" data-mp="2l0kaftr1zo86">
              <div className="overflow-hidden rounded-card bg-surface shadow-[0_20px_50px_rgba(0,0,0,0.3)]" data-id="element-2238" data-mp="2l029ijtg4ltx">
                <div className="flex gap-3.5 px-4 py-4" data-id="element-2239" data-mp="2jkq3gqrs1lmf">
                  <img src={demoIdentity.photo} alt="" loading="lazy" className="h-[4.5rem] w-[3.5rem] shrink-0 rounded-lg object-cover" data-id="element-2240" data-mp="2ek2aw0nwgwq3" />
                  <div className="min-w-0" data-id="element-2241" data-mp="24nyym1zc3hpf">
                    <p className="truncate text-body-sm font-bold uppercase tracking-wide text-ink" data-id="element-2242" data-mp="32afvj645ntan">
                      {demoIdentity.name}
                    </p>
                    <p className="mt-1 truncate text-caption text-ink-muted" data-id="element-2243" data-mp="2xcwfc0nwd6g2">
                      {demoIdentity.institution} · {demoIdentity.campus}
                    </p>
                    <p className="truncate text-caption text-ink-muted" data-id="element-2244" data-mp="2nkgyx7s90pxa">{demoIdentity.programme}</p>
                    <p className="text-caption text-ink-muted" data-id="element-2245" data-mp="3l1yvf42vur8g">{demoIdentity.level}</p>
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-caption font-semibold text-brand-800" data-id="element-2246" data-mp="3g8w1guu71frq">
                      <BadgeCheckIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2247" data-mp="3sjx08va4de5c" />
                      Verified student
                    </span>
                  </div>
                </div>
                <div className="flex h-7 items-end gap-[2px] overflow-hidden border-t border-line px-4 pb-2 pt-2" data-id="element-2248" data-mp="1fymlrtmzfyg">
                  {/* A credential's machine-readable strip, drawn rather than faked as an image. */}
                  {Array.from({
                  length: 48
                }).map((_, index) => <span key={index} aria-hidden className="block h-3 bg-ink" style={{
                  width: index % 3 === 0 ? 2 : 1,
                  opacity: index % 4 === 0 ? 1 : 0.7
                }} data-id="element-2249" data-mp="fbaiw3nfxehh" data-mpi={index} />)}
                </div>
              </div>
              <p className="mt-2.5 text-center text-caption text-brand-100" data-id="element-2250" data-mp="z14gwzgr0pu5">
                Demonstration credential
              </p>
            </div>

            {/* Statement */}
            <div className="max-w-[40ch]" data-id="element-2251" data-mp="1vqeogvms4uwc">
              <h2 id="identity-heading" className="text-h2 font-bold tracking-tight text-white" data-id="element-2252" data-mp="1qsmxc3tylct7">
                One identity.
                <br data-id="element-2253" data-mp="25hrfxm4o1j1u" />
                Every campus experience.
              </h2>
              <p className="mt-4 text-body leading-7 text-brand-100" data-id="element-2254" data-mp="20kso5a8h5o6t">
                Campuzon connects your identity to academics, housing, services, opportunities and
                everything that matters on campus.
              </p>
              <p className="mt-3 max-w-[46ch] text-body-sm text-brand-100/80" data-id="element-2255" data-mp="17x56o42i3doe">
                Verified student states that an institution confirmed your enrolment. It is not a
                statement about your grades, your conduct or anything else.
              </p>
            </div>

            {/* Connected ecosystem */}
            <div className="relative mx-auto h-64 w-64 shrink-0" data-id="element-2256" data-mp="2t8ohq62qyzzd">
              <div aria-hidden className="absolute inset-6 rounded-full border border-dashed border-white/25" data-id="element-2257" data-mp="2ta81w7rixv6f" />
              <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white" data-id="element-2258" data-mp="35rsl4bqbs4dp">
                <UsersIcon aria-hidden className="h-7 w-7 text-brand-800" data-id="element-2259" data-mp="2vsl2b90tnhj7" />
              </span>
              {CONNECTED.map(node => {
              const Icon = node.icon;
              return <span key={node.label} title={node.label} className={`absolute flex h-11 w-11 items-center justify-center rounded-full bg-ink ring-1 ring-white/20 ${node.position}`} data-id="element-2260" data-mp="3ogz31wjaoryg">
                    <Icon aria-hidden className="h-5 w-5 text-white" data-id="element-2261" data-mp="3epcktg8jhsm7" />
                    <span className="sr-only" data-id="element-2262" data-mp="6au8317nlow9">{node.label}</span>
                  </span>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}